import { useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { connectService } from '@/services/connectService';
import { ConnectionData, ConnectError, ConnectErrorType } from '@/types/connect.types';
import { isDebug } from '@/config/constants';

export interface UseConnectReturn {
  startCall: (sessionId: string, featureName?: string, queueId?: string) => Promise<ConnectionData>;
  endCall: (sessionId: string) => Promise<void>;
  isLoading: boolean;
  error: ConnectError | null;
}

/**
 * Hook for Amazon Connect API integration
 * 
 * Provides a simple interface for starting WebRTC calls with:
 * - Automatic credential management via useAuth
 * - Loading and error state management
 * - Automatic retry on transient failures (handled by connectService)
 * 
 * @example
 * ```tsx
 * const { startCall, isLoading, error } = useConnect();
 * 
 * const handleStartCall = async () => {
 *   try {
 *     const connectionData = await startCall('session-123', 'Feature Demo');
 *     // Use connectionData to initialize Chime SDK
 *   } catch (err) {
 *     // Error is already set in state
 *     console.error('Failed to start call:', err);
 *   }
 * };
 * ```
 */
export function useConnect(): UseConnectReturn {
  const { getCredentials } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ConnectError | null>(null);

  /**
   * Start a WebRTC call to Amazon Connect
   * 
   * @param sessionId - Unique session identifier
   * @param featureName - Optional feature context for the call
   * @param queueId - Optional queue ID if coming from queue system
   * @returns Connection data for initializing Chime SDK
   * @throws ConnectError with type and retryable flag
   */
  const startCall = useCallback(
    async (
      sessionId: string,
      featureName?: string,
      queueId?: string
    ): Promise<ConnectionData> => {
      // Reset error state
      setError(null);
      setIsLoading(true);
      const t0 = performance.now();

      try {
        // Get fresh credentials from Amplify
        const credentials = await getCredentials();
        const tCreds = performance.now();

        const connectionData = await connectService.startWebRTCCall(
          credentials,
          sessionId,
          featureName,
          queueId
        );
        const tApi = performance.now();

        if (isDebug()) console.log(`⏱ Call Setup - Credentials: ${Math.round(tCreds - t0)}ms, API Call: ${Math.round(tApi - tCreds)}ms`);
        setIsLoading(false);
        return connectionData;

      } catch (err) {
        // Transform error to ConnectError if needed
        let connectError: ConnectError;
        
        if (err instanceof Error && 'type' in err && 'retryable' in err) {
          connectError = err as ConnectError;
        } else {
          connectError = {
            type: ConnectErrorType.CONNECT_ERROR,
            message: err instanceof Error ? err.message : 'Unknown error',
            retryable: false,
            originalError: err instanceof Error ? err : undefined,
          };
        }

        setError(connectError);
        setIsLoading(false);
        throw connectError;
      }
    },
    [getCredentials]
  );

  /**
   * End a call and clean up the session
   * 
   * @param sessionId - Session ID to end
   */
  const endCall = useCallback(
    async (sessionId: string): Promise<void> => {
      try {
        const credentials = await getCredentials();
        await connectService.endCall(credentials, sessionId);
      } catch (err) {
        console.error('Failed to end call:', err);
        // Don't throw - cleanup is best-effort
      }
    },
    [getCredentials]
  );

  return {
    startCall,
    endCall,
    isLoading,
    error,
  };
}
